/**
 * ジョブ詳細 `/jobs/$jobId`。
 *
 * `jobId` は `provisioning_jobs.id`（ULID）。BullMQ の内部 jobId は外に出さない（§4.3）。
 * 失敗時のスタックは**運営者にだけ**見せる。契約者パネルは `userMessage` しか受け取らない。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, XCircle } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  JobTimeline,
  KeyValue,
  PageHeader,
  Section,
  StatusBadge,
  formatDateTime,
} from '@mail-sv/ui';
import { labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { formatDuration } from '../lib/format.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { CodeBlock, JsonBlock } from '../components/code-block.js';
import { DetailSkeleton, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type { JobDetail } from '../types/api.js';

const routeApi = getRouteApi(RID.jobDetail);

export function JobDetailPage(): React.ReactElement {
  const { jobId } = routeApi.useParams();
  const qc = useQueryClient();
  const canCommand = usePermission(OPS_PERMISSION.nodeCommand);

  const query = useQuery({
    queryKey: qk.job(jobId),
    queryFn: () => api.get<JobDetail>(`/jobs/provisioning/${jobId}`),
    // 実行中だけ 2 秒。終端に達したら止める。
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 2_000 : false),
  });

  const retry = useMutation({
    mutationFn: () => api.post(`/jobs/provisioning/${jobId}/retry`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.job(jobId) }),
  });
  const cancel = useMutation({
    mutationFn: () => api.post(`/jobs/provisioning/${jobId}/cancel`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.job(jobId) }),
  });

  const job = query.data;
  const cancellable = job?.status === 'queued' || job?.status === 'running';

  return (
    <>
      <PageHeader
        title={job ? labelOf(job.kind) : 'ジョブ'}
        description={job ? `ジョブ ID: ${job.id}` : undefined}
        breadcrumbs={[{ label: 'ジョブ', href: '/jobs' }, { label: job ? labelOf(job.kind) : '…' }]}
        actions={
          <>
            <RefetchingDot active={query.isFetching && !query.isLoading} />
            {job ? <StatusBadge status={job.status} dot pulse={job.status === 'running'} /> : null}
            {canCommand && job ? (
              <>
                <Button variant="outline" size="sm" onClick={() => retry.mutate()} loading={retry.isPending}>
                  <RotateCcw aria-hidden />
                  再実行
                </Button>
                {cancellable ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => cancel.mutate()}
                    loading={cancel.isPending}
                    className="text-destructive"
                  >
                    <XCircle aria-hidden />
                    キャンセル
                  </Button>
                ) : null}
              </>
            ) : null}
          </>
        }
      />

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.isLoading ? <DetailSkeleton /> : null}

      {job ? (
        <div className="space-y-6">
          {job.status === 'failed' && job.lastErrorCode ? (
            <Alert variant="destructive">
              <AlertTitle>ジョブが失敗しました（{job.lastErrorCode}）</AlertTitle>
              <AlertDescription>
                {job.userMessage ?? '詳細は下のエラー内容を確認してください。再実行は冪等です。'}
              </AlertDescription>
            </Alert>
          ) : null}

          <Section title="概要">
            <div className="rounded-lg border border-border bg-card p-4">
              <KeyValue
                columns={3}
                items={[
                  { key: '種別', value: labelOf(job.kind) },
                  { key: '状態', value: <StatusBadge status={job.status} size="sm" /> },
                  { key: '試行回数', value: `${job.attempts} / ${job.maxAttempts}` },
                  {
                    key: '対象',
                    value: job.subjectId ? `${job.subjectType} / ${job.subjectId}` : job.subjectType,
                  },
                  {
                    key: '契約組織',
                    value: job.organizationId ? (
                      <Link
                        to="/organizations/$orgId"
                        params={{ orgId: job.organizationId }}
                        search={{ tab: 'overview' }}
                        className="hover:text-primary"
                      >
                        {job.organizationName ?? job.organizationId}
                      </Link>
                    ) : (
                      '—'
                    ),
                  },
                  { key: '開始', value: formatDateTime(job.startedAt) },
                  { key: '終了', value: formatDateTime(job.finishedAt) },
                  { key: '所要', value: formatDuration(job.durationMs) },
                ]}
              />
            </div>
          </Section>

          <Section title="進行">
            <div className="rounded-lg border border-border bg-card p-4">
              <JobTimeline
                jobStatus={job.status}
                progress={job.progress}
                steps={job.steps.map((s) => ({
                  id: String(s.seq),
                  name: s.stepKey,
                  status: s.status,
                  startedAt: s.startedAt,
                  finishedAt: s.finishedAt,
                  message: s.errorMessage,
                  attempt: s.attempts,
                }))}
              />
            </div>
          </Section>

          <Section title="入力パラメータ">
            <JsonBlock value={job.input} maxHeight="20rem" />
          </Section>

          {job.lastError ? (
            <Section
              title="エラー内容"
              description="運営者にのみ表示されます。契約者パネルには userMessage しか渡りません。"
            >
              <CodeBlock value={job.lastError} language="stacktrace" maxHeight="24rem" filename={`job-${job.id}.log`} />
            </Section>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
